import { useContext, useState } from 'react';

import { Box, Card, Text } from '@gympass/yoga';
import { ChevronUp, ChevronDown } from '@gympass/yoga-icons';
import withAuth from 'hocs/withAuth/withAuth';
import BookIcon from 'icons/book.svg';
import BulbIcon from 'icons/bulb.svg';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { SpecialistContext, useModals } from 'context';
import { useOnInit, useRequest } from 'hooks';
import {
  ActivitiesTopicsResponse,
  EndUserState,
  getEndUserInfo,
  getTopics,
  getTopicsPage,
  TopicsPage,
} from 'services';

import {
  TabList,
  Loading,
  HeaderUserInfo,
  ActivitiesTable,
  Paginator,
} from 'components';
import { ConfirmComponentProps } from 'modals/Confirm';
import { DeliverContentModalComponentProps } from 'modals/DeliverContent';

import * as S from './styles';

const Activities: React.FC = () => {
  const [programCursors] = useState<{ [key: string]: string }>({ 1: '' });
  const [userProgram, setUserProgram] = useState<ActivitiesTopicsResponse>();
  const [pagesProgram, setPagesProgram] = useState<TopicsPage[]>([]);

  const [userInfo, setUserInfo] = useState<EndUserState>();
  const [topicsCursors] = useState<{ [key: string]: string }>({ 1: '' });
  const [userTopics, setUserTopics] = useState<ActivitiesTopicsResponse>();
  const [pagesTopics, setPagesTopics] = useState<TopicsPage[]>([]);
  const [clicked, setClicked] = useState<number | null>();
  const request = useRequest();

  const { openModal } = useModals();

  const { id = '' } = useParams<{
    id: string;
  }>();

  const { specialistInfo } = useContext(SpecialistContext);
  const navigate = useNavigate();

  const getIcon = (typeTopic: keyof typeof icons) => {
    const icons = {
      book: <BookIcon />,
      bulb: <BulbIcon />,
    };
    return icons[typeTopic] || null;
  };

  const getUserInfo = async () => {
    request.setLoading();
    try {
      const { data } = await getEndUserInfo(id);
      setUserInfo(data);
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const getUserProgram = async (currentPage: number) => {
    request.setLoading();
    try {
      const enrolled = true;
      const defaultLimit = 4;
      const { data } = await getTopics(
        id,
        programCursors[currentPage],
        enrolled,
        defaultLimit,
      );
      setUserProgram(data);
      if (data.metadata.nextCursor !== '') {
        programCursors[currentPage + 1] = data.metadata.nextCursor;
      }
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const getActivitiesUser = async (currentPage: number) => {
    request.setLoading();
    try {
      const { data } = await getTopics(id, topicsCursors[currentPage]);
      setUserTopics(data);
      if (data.metadata.nextCursor !== '') {
        topicsCursors[currentPage + 1] = data.metadata.nextCursor;
      }
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const getTopicsPagesUser = async (
    cognitoId: string,
    topicId: number,
    programPages?: boolean,
  ) => {
    request.setLoading();
    try {
      const { data } = await getTopicsPage(id, topicId);
      if (programPages) setPagesProgram(data);
      else setPagesTopics(data);
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const toggle = async (index: number, programPages?: boolean) => {
    if (clicked === index) {
      return setClicked(null);
    }
    await getTopicsPagesUser(id, index, programPages);
    return setClicked(index);
  };

  const handleOpenDeliverContentModal = (isEnrollment = false) => {
    openModal<DeliverContentModalComponentProps>('deliverContent', {
      props: {
        endUserId: id,
        onAfterSubmit: () => {
          getUserInfo();
          getActivitiesUser(1);
          getUserProgram(1);
        },
        isEnrollment,
        enrolledProgramId: userProgram?.results[0]?.programId,
      },
    });
  };

  const ShowBanner: React.FC<{
    program?: boolean;
    onClickProp: () => void;
  }> = ({ program, onClickProp }) => {
    const [title] = useState<string>(
      program ? 'ENDUSER.ACTIVITIES.ACTIVEPROGRAM' : 'ENDUSER.ACTIVITIES.TITLE',
    );
    const [noActive] = useState<string>(
      program
        ? 'ENDUSER.ACTIVITIES.NOACTIVEPROGRAM'
        : 'ENDUSER.ACTIVITIES.NOACTIVEMODULES',
    );

    const [subscribe] = useState<string>(
      program
        ? 'ENDUSER.ACTIVITIES.SUBSCRIBETOPROGRAM'
        : 'ENDUSER.ACTIVITIES.SUBSCRIBETOMODULE',
    );

    const [textButton] = useState<string>(
      program
        ? 'ENDUSER.ACTIVITIES.SUBSCRIBETOPROGRAMBUTTON'
        : 'ENDUSER.ACTIVITIES.SUBSCRIBETOMODULEBUTTON',
    );

    return (
      <S.ActivitiesSection data-testid="programSectionBanner">
        <Card.Header>
          <S.ActivitiesHeaderContent>
            <Text.H5 data-testid="programTitleBanner">
              <FormattedMessage id={title} />
            </Text.H5>
          </S.ActivitiesHeaderContent>
        </Card.Header>
        <S.NoProgramContent>
          <S.NoProgramImage>
            {getIcon(program ? 'book' : 'bulb')}
          </S.NoProgramImage>
          <S.NoProgramText>
            <Text.Medium>
              <FormattedMessage id={noActive} />
            </Text.Medium>
            <S.NoProgramDescriptionText>
              <Text.Small>
                <FormattedMessage id={subscribe} />
              </Text.Small>
            </S.NoProgramDescriptionText>
            <S.ButtonSubscribeToProgram
              small
              data-testid={
                program ? 'buttonSubscribeToProgram' : 'buttonSubscribeToModule'
              }
              onClick={onClickProp}
            >
              <FormattedMessage id={textButton} />
            </S.ButtonSubscribeToProgram>
          </S.NoProgramText>
        </S.NoProgramContent>
      </S.ActivitiesSection>
    );
  };

  const handleOpenChangeProgramModal = () => {
    openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: 'ENDUSER.ACTIVITIES.CHANGEACTIVEPROGRAM',
        confirmLabel: 'DELIVERCONTENT.MODAL.CONTINUE',
        cancelLabel: 'DELIVERCONTENT.MODAL.CANCEL',
        hideIcon: true,
        onConfirm: () => handleOpenDeliverContentModal(true),
      },
    });
  };

  useOnInit(() => {
    if (specialistInfo?.geoCode !== 'BR') {
      navigate('/care-provider/info');
      return;
    }
    getUserInfo();
    getActivitiesUser(1);
    getUserProgram(1);
  });

  const renderPrograms = () => {
    if (userProgram?.results && userProgram?.results.length > 0) {
      return (
        <S.ProgramSection data-testid="programSection">
          <Card.Header>
            <S.ProgramHeaderContent>
              <div>
                <S.ProgramName data-testid="programTitle">
                  {userProgram?.results[0]?.program}
                </S.ProgramName>
                <S.ActiveProgram>
                  <FormattedMessage id="ENDUSER.ACTIVITIES.ACTIVEPROGRAM" />
                </S.ActiveProgram>
              </div>
              <S.ButtonChangeProgram
                small
                data-testid="buttonChangeProgram"
                onClick={handleOpenChangeProgramModal}
              >
                <FormattedMessage id="PROGRAM.BUTTON.CHANGE" />
              </S.ButtonChangeProgram>
            </S.ProgramHeaderContent>
          </Card.Header>
          {userProgram?.results.map(topic => (
            <S.ActivitiesContent key={topic.id}>
              <S.ActivitiesTopics>
                <Box>
                  <S.TopicsTitle>{topic.titleKey}</S.TopicsTitle>
                  <S.ProgressBar
                    isCompleted={
                      topic.progress.acknowledged === topic.progress.total
                    }
                    max={topic.progress.total}
                    variant="primary"
                    value={topic.progress.acknowledged}
                    label={{
                      value: `${topic.progress.acknowledged}/${topic.progress.total} atividades finalizadas`,
                      placement: 'left',
                    }}
                  />
                </Box>
                <S.TopicsButton onClick={() => toggle(topic.id, true)} small>
                  <FormattedMessage id="ACTIVITIES.LIST.VIEW.BUTTON" />
                  {clicked !== topic.id ? <ChevronDown /> : <ChevronUp />}
                </S.TopicsButton>
              </S.ActivitiesTopics>

              <S.Dropdown isCollapsed={clicked === topic.id}>
                <ActivitiesTable data={pagesProgram} cognitoId={id} />
              </S.Dropdown>
            </S.ActivitiesContent>
          ))}
          <S.Footer>
            <Paginator
              next={(currentPage: number) => getUserProgram(currentPage + 1)}
              previous={(currentPage: number) =>
                getUserProgram(currentPage - 1)
              }
              lastPage={userProgram?.metadata?.pages}
            />
          </S.Footer>
        </S.ProgramSection>
      );
    }

    return (
      <ShowBanner
        program
        onClickProp={() => handleOpenDeliverContentModal(true)}
      />
    );
  };

  return (
    <S.UserInfoContainer>
      <Loading isLoading={request.isLoading()} />
      <HeaderUserInfo user={userInfo} />
      <S.Divisor />
      <S.TabsContainer data-testid="tabActivitiesTabs">
        <TabList selectedIndex={4} />
        {renderPrograms()}
        {userTopics?.results && userTopics?.results.length > 0 ? (
          <S.ActivitiesSection data-testid="activitiesSection">
            <Card.Header>
              <S.ActivitiesHeaderContent>
                <Text.H5 data-testid="activitiesTitle">
                  <FormattedMessage id="ENDUSER.ACTIVITIES.TITLE" />
                </Text.H5>
                <S.ButtonAddActivities
                  small
                  data-testid="buttonAddActivities"
                  onClick={() => handleOpenDeliverContentModal()}
                >
                  <FormattedMessage id="ACTIVITIES.BUTTON.ADD" />
                </S.ButtonAddActivities>
              </S.ActivitiesHeaderContent>
            </Card.Header>
            {userTopics?.results.map(topic => (
              <S.ActivitiesContent key={topic.id}>
                <S.ActivitiesTopics>
                  <Box>
                    <S.TopicsTitle>{topic.titleKey}</S.TopicsTitle>
                    <S.ProgressBar
                      isCompleted={
                        topic.progress.acknowledged === topic.progress.total
                      }
                      max={topic.progress.total}
                      variant="primary"
                      value={topic.progress.acknowledged}
                      label={{
                        value: `${topic.progress.acknowledged}/${topic.progress.total} atividades finalizadas`,
                        placement: 'left',
                      }}
                    />
                  </Box>
                  <S.TopicsButton onClick={() => toggle(topic.id)} small>
                    <FormattedMessage id="ACTIVITIES.LIST.VIEW.BUTTON" />
                    {clicked !== topic.id ? <ChevronDown /> : <ChevronUp />}
                  </S.TopicsButton>
                </S.ActivitiesTopics>

                <S.Dropdown isCollapsed={clicked === topic.id}>
                  <ActivitiesTable data={pagesTopics} cognitoId={id} />
                </S.Dropdown>
              </S.ActivitiesContent>
            ))}
            <S.Footer>
              <Paginator
                next={(currentPage: number) =>
                  getActivitiesUser(currentPage + 1)
                }
                previous={(currentPage: number) =>
                  getActivitiesUser(currentPage - 1)
                }
                lastPage={userTopics?.metadata?.pages}
              />
            </S.Footer>
          </S.ActivitiesSection>
        ) : (
          <ShowBanner onClickProp={() => handleOpenDeliverContentModal()} />
        )}
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(Activities);
